import { useMemo, useContext } from 'react';
import { MeContext } from '../../../../context/MeContext';
import { MeContextState } from '../../../../store/types/account';

interface PassTemplate {
  id?: string;
  uuid?: string;
  name: string;
  entity_id?: string | number;
}

interface EntityWithId {
  uuid: string;
  id?: string | number;
  name: string;
}

export const useTemplateNameValidation = (
  isEdit: boolean,
  currentTemplateUuid?: string,
  currentEntityUuid?: string
) => {
  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const meState = meStateRaw;

  const meStateWithTemplates = meState as MeContextState & {
    passTemplates?: PassTemplate[];
    entities?: EntityWithId[];
  };

  const currentEntity = useMemo(() => {
    if (!currentEntityUuid || !meStateWithTemplates?.entities) return null;
    return meStateWithTemplates.entities.find(
      (entity: EntityWithId) => entity.uuid === currentEntityUuid
    );
  }, [currentEntityUuid, meStateWithTemplates?.entities]);

  const templatesForCurrentEntity = useMemo(() => {
    if (!meStateWithTemplates?.passTemplates) return [];

    if (!currentEntityUuid || !currentEntity) {
      return meStateWithTemplates.passTemplates;
    }

    const currentEntityId = currentEntity.id;

    return meStateWithTemplates.passTemplates.filter((template: PassTemplate) => {
      if (template.entity_id !== undefined && template.entity_id !== null) {
        return String(template.entity_id) === String(currentEntityId);
      }
      return true;
    });
  }, [meStateWithTemplates?.passTemplates, currentEntityUuid, currentEntity]);

  const templateNames = useMemo(
    () =>
      templatesForCurrentEntity.map(
        (passTemplate: PassTemplate) => passTemplate.name
      ),
    [templatesForCurrentEntity]
  );

  const originalTemplate = useMemo(
    () =>
      isEdit && currentTemplateUuid
        ? meStateWithTemplates?.passTemplates?.find(
            (passTemplate: PassTemplate) =>
              passTemplate.id === currentTemplateUuid ||
              passTemplate.uuid === currentTemplateUuid
          )
        : null,
    [isEdit, currentTemplateUuid, meStateWithTemplates?.passTemplates]
  );
  const originalTemplateName = originalTemplate?.name;

  const isTemplateNameUnique = (value: string): boolean => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) return true;

    if (
      isEdit &&
      originalTemplateName &&
      trimmedValue === originalTemplateName.trim()
    ) {
      return true;
    }

    return !templateNames.some(
      (name: string) => name?.trim() === trimmedValue
    );
  };

  return {
    isTemplateNameUnique,
    originalTemplateName,
  };
};
